import { agent } from '@shared/services/agent';
import {
  MeetingRoom,
  MeetingRoomBooking,
  MeetingRoomBookingResponse,
} from '@features/meeting-rooms/pages/models/MeetingRooms';
import { DATE_FORMAT } from "@shared/utils/DateUtils";
import { OfficeEntity } from "./AdvanceHotDeskingService";
import { HospitalityBookingRequest } from '@features/meeting-rooms/pages/models/hospitality.models';
import { BasicUserInfo } from '@shared/models/user.models';
import { Recurrence } from '@features/meeting-rooms/pages/models/meeting-room-booking-dialog.duck';
import { Moment } from "moment";
import moment from "moment";

const V2_BASE = '/api/v2/meeting-rooms';

function convertBookingTimesToLocal(bookings: MeetingRoomBooking[]): MeetingRoomBooking[] {
  return bookings.map(b => ({
    ...b,
    startDateTime: moment.utc(b.startDateTime).local(),
    endDateTime: moment.utc(b.endDateTime).local(),
  }));
}

export async function bookMeetingRoomV2(meetingRoom: MeetingRoom,
                                         fromDateTime: Moment,
                                         toDateTime: Moment,
                                         meetingName: string,
                                         addToCalendar: boolean,
                                         isTeamsMeeting: boolean,
                                         hospitalityBookingRequest?: HospitalityBookingRequest,
                                         overriddenHostUser?: BasicUserInfo,
                                         meetingBodyText?: string,
                                         outlookEventUid?: string,
                                         recurrence?: Recurrence,
                                         recurrenceEndDate?: Moment,
                                         applyToSeries?: boolean): Promise<void> {
  if (toDateTime.isBefore(moment())) {
    throw new Error('Cannot book a meeting room in the past');
  }
  await agent.requests.post(`${V2_BASE}/booking`, {
    meetingRoomId: meetingRoom.id,
    fromDateTime: fromDateTime.toISOString(true),
    toDateTime: toDateTime.toISOString(true),
    name: meetingName,
    updateCalendar: addToCalendar,
    hospitalityBooking: hospitalityBookingRequest,
    overriddenHostUserId: overriddenHostUser?.userId,
    bodyText: meetingBodyText,
    isTeamsMeeting: isTeamsMeeting,
    outlookEventUid: outlookEventUid,
    recurrence: recurrence,
    recurrenceEndDate: recurrenceEndDate?.format(DATE_FORMAT) ?? null,
    applyToSeries: applyToSeries ?? true,
  });
}

export async function updateMeetingRoomBookingV2(bookingId: number,
                                                  meetingRoom: MeetingRoom,
                                                  fromDateTime: Moment,
                                                  toDateTime: Moment,
                                                  meetingName: string,
                                                  addToCalendar: boolean,
                                                  isTeamsMeeting: boolean,
                                                  hospitalityBookingRequest?: HospitalityBookingRequest,
                                                  overriddenHostUser?: BasicUserInfo,
                                                  meetingBodyText?: string,
                                                  recurrence?: Recurrence,
                                                  recurrenceEndDate?: Moment,
                                                  applyToSeries?: boolean): Promise<void> {
  await agent.requests.put(`${V2_BASE}/booking`, {
    bookingId: bookingId,
    meetingRoomId: meetingRoom.id,
    fromDateTime: fromDateTime.toISOString(true),
    toDateTime: toDateTime.toISOString(true),
    name: meetingName,
    updateCalendar: addToCalendar,
    hospitalityBooking: hospitalityBookingRequest,
    overriddenHostUserId: overriddenHostUser?.userId,
    bodyText: meetingBodyText,
    isTeamsMeeting: isTeamsMeeting,
    recurrence: recurrence,
    recurrenceEndDate: recurrenceEndDate?.format(DATE_FORMAT) ?? null,
    applyToSeries: !!applyToSeries,
  });
}

export async function getMeetingRoomBookingsV2(meetingRoom: MeetingRoom, date: Moment): Promise<MeetingRoomBookingResponse> {
  let url = `/api/v2/meeting-room/${meetingRoom.id}/bookings?date=${date.format(DATE_FORMAT)}`;
  url += `&timezone=${new Date().getTimezoneOffset()}`;
  const response = await agent.requests.get(url);
  const retVal = {
    ...response,
    meetingRoomBookings: convertBookingTimesToLocal(response.meetingRoomBookings),
  };
  return retVal;
}

export async function getMeetingRoomBookingsForOfficeV2(office: OfficeEntity, date: Moment): Promise<MeetingRoomBookingResponse[]> {
  let url = `${V2_BASE}/bookings/office/${office.id}?date=${date.format(DATE_FORMAT)}`;
  url += `&timezone=${new Date().getTimezoneOffset()}`;
  const response: MeetingRoomBookingResponse[] = await agent.requests.get(url);
  const retVal = response.map(r => ({
    ...r,
    meetingRoomBookings: convertBookingTimesToLocal(r.meetingRoomBookings),
  }));
  return retVal;
}

export async function cancelMeetingRoomBookingV2(bookingId: number, outlookSeriesMasterId?: string): Promise<void> {
  const query = outlookSeriesMasterId ? `?outlookSeriesMasterId=${encodeURIComponent(outlookSeriesMasterId)}` : '';
  await agent.requests.del(`${V2_BASE}/booking/${bookingId}/cancel${query}`);
}

export async function cancelOutlookMeetingBookingV2(roomEmailAddress: string, outlookEventUid: string): Promise<void> {
  await agent.requests.del(`${V2_BASE}/booking/outlook/${roomEmailAddress}/${outlookEventUid}/cancel`);
}

export async function cancelOccurrenceMeetingRoomBookingV2(outlookOccurrenceId: string, bookingId: number): Promise<void> {
  await agent.requests.del(`${V2_BASE}/booking/occurrence/${outlookOccurrenceId}/${bookingId}/cancel`);
}
